import React, { useCallback, useState } from 'react'
import { FieldHookConfig } from 'formik'
import usePlacesAutocomplete from 'use-places-autocomplete'
import { useField } from '@/hooks/useField'

import ErrorMessage from '@/components/formik/messages/ErrorMessage'
import AutocompleteInput, { AutocompleteInputProps, AutocompleteValue } from '@/components/ui/inputs/AutocompleteInput'

const MemoizedAutocompleteInput = React.memo(AutocompleteInput)

export type GooglePlacesAutocompleteValue = { description: string; placeId: string } | null

export type GooglePlacesAutocompleteFieldProps = Omit<AutocompleteInputProps, 'onChange' | 'options'> & FieldHookConfig<GooglePlacesAutocompleteValue>

export function GooglePlacesAutocompleteField(props: GooglePlacesAutocompleteFieldProps) {
  const { multiple, name, ...rest } = props
  const [field, meta, helpers] = useField<GooglePlacesAutocompleteValue>(name)

  const {
    value: searchValue,
    setValue: setSearchValue,
    suggestions: { data },
  } = usePlacesAutocomplete({ debounce: 300 })

  const [autoCompleteValue, setAutoCompleteValue] = useState<AutocompleteValue>(
    field.value ? { label: field.value.description, value: field.value.placeId } : null
  )
  const [autocompleteInputValue, setAutocompleteInputValue] = useState('')

  const handleAutocompleteValueChange = useCallback(
    (newValue: AutocompleteValue) => {
      if (Array.isArray(newValue)) {
        throw new Error(`${JSON.stringify(newValue)} should not be an array`)
      }
      setAutoCompleteValue(newValue)
      helpers.setValue(newValue ? { description: newValue.label, placeId: newValue.value } : null)
    },
    [helpers]
  )

  return (
    <MemoizedAutocompleteInput
      {...rest}
      error={!!meta.error && meta.touched}
      freeSolo={false}
      inputValue={autocompleteInputValue}
      helperText={!!meta.error && meta.touched && <ErrorMessage error={meta.error} />}
      multiple={multiple}
      name={field.name}
      options={
        !data.length && autoCompleteValue !== null
          ? Array.isArray(autoCompleteValue)
            ? autoCompleteValue
            : [autoCompleteValue]
          : data.map((suggestion) => ({
              label: suggestion.description,
              value: suggestion.place_id,
            }))
      }
      onBlur={field.onBlur}
      onChange={handleAutocompleteValueChange}
      onInputChange={(inputValue: string) => {
        setAutocompleteInputValue(inputValue)
        setSearchValue(inputValue)
      }}
      value={autoCompleteValue}
    />
  )
}

export default GooglePlacesAutocompleteField
